import {
  DEFAULT_LOCALE,
  dirFor,
  interpolate,
  LOCALES,
  messagesFor,
  type Locale,
  type Messages,
} from "@kmstories/shared";
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";

/**
 * Direction is a DOCUMENT-level concern: one effect flips <html lang dir>, and
 * every layout rule below is written in logical properties, so the fa locale
 * mirrors without a second stylesheet. The locale lives in the URL (`/fa/…`)
 * so a shared link keeps its language; localStorage only remembers the
 * visitor's last choice for the next visit.
 */
interface LocaleContextValue {
  locale: Locale;
  messages: Messages;
  /** Prefix a locale-free path: lp("/share") → "/fa/share" in Persian. */
  lp: (path: string) => string;
  /** The same page in the other language. */
  switchTo: (locale: Locale) => void;
  t: (template: string, vars?: Record<string, string | number>) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);
const STORAGE_KEY = "kms.locale";

export function localizedPath(path: string, locale: Locale): string {
  const clean = path === "/" ? "" : path.replace(/\/+$/, "");
  if (locale === "fa") return clean ? `/fa${clean}` : "/fa";
  return clean || "/";
}

/** "/fa/share" → { locale: "fa", path: "/share" } */
export function splitLocale(pathname: string): { locale: Locale; path: string } {
  if (pathname === "/fa" || pathname.startsWith("/fa/")) return { locale: "fa", path: pathname.slice(3) || "/" };
  return { locale: DEFAULT_LOCALE, path: pathname || "/" };
}

export function readStoredLocale(): Locale | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return (LOCALES as readonly string[]).includes(v ?? "") ? (v as Locale) : null;
  } catch {
    return null;
  }
}

export function LocaleProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dirFor(locale);
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // private mode; the URL still carries the language
    }
  }, [locale]);

  const messages = useMemo(() => messagesFor(locale), [locale]);
  const lp = useCallback((path: string) => localizedPath(path, locale), [locale]);
  const switchTo = useCallback(
    (next: Locale) => {
      const { path } = splitLocale(location.pathname);
      navigate(localizedPath(path, next) + location.search + location.hash);
    },
    [location.hash, location.pathname, location.search, navigate],
  );
  const t = useCallback((template: string, vars?: Record<string, string | number>) => interpolate(template, vars), []);

  const value = useMemo(() => ({ locale, messages, lp, switchTo, t }), [locale, messages, lp, switchTo, t]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale outside LocaleProvider");
  return ctx;
}

/** Shorthand: the messages object plus helpers, the way most components want it. */
export function useT() {
  const { messages, locale, lp, t } = useLocale();
  return { m: messages, locale, lp, t, rtl: locale === "fa" };
}
